import { useEffect, useState } from 'react'
import {
  Button,
  Chip,
  Field,
  Input,
  PageHead,
  StateBlock,
  Toggle,
} from '@w7/ui'
import { hasLivePlayerMfa } from '@/api/client'
import { getAppBuildId } from '@/version/clientConfig'
import { useAppToast } from '@/ui/toaster'
import {
  useClientConfigAdmin,
  useResetClientConfig,
  useSaveClientConfig,
} from './hooks'
import type { ClientConfigPolicy } from './types'
import './version.css'

function clonePolicy(p: ClientConfigPolicy): ClientConfigPolicy {
  return {
    frontendUi: { ...p.frontendUi },
    backendUi: { ...p.backendUi },
  }
}

function ToggleRow({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string
  hint: string
  checked: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <div className="w7-version-toggle">
      <Toggle checked={checked} onChange={onChange} label={label} />
      <div>
        <div className="w7-version-toggle-label">{label}</div>
        <p className="w7-dim w7-version-lead">{hint}</p>
      </div>
    </div>
  )
}

export function VersionSettingsPage() {
  const toast = useAppToast()
  const live = hasLivePlayerMfa()
  const query = useClientConfigAdmin()
  const save = useSaveClientConfig()
  const reset = useResetClientConfig()
  const [draft, setDraft] = useState<ClientConfigPolicy | null>(null)
  const thisBuildId = getAppBuildId()

  useEffect(() => {
    if (query.data?.policy) setDraft(clonePolicy(query.data.policy))
  }, [query.data])

  if (query.isLoading || !draft) {
    return (
      <StateBlock icon="settings" title="Loading version policy…">
        Reading Player-API client-config.
      </StateBlock>
    )
  }

  if (query.isError) {
    return (
      <StateBlock icon="error" title="Could not load version policy">
        {query.error instanceof Error ? query.error.message : 'Unknown error'}
      </StateBlock>
    )
  }

  const source = query.data?.source ?? 'env'
  const updatedAt = query.data?.updatedAt

  const patchFrontend = (partial: Partial<ClientConfigPolicy['frontendUi']>) => {
    setDraft((prev) =>
      prev
        ? { ...prev, frontendUi: { ...prev.frontendUi, ...partial } }
        : prev,
    )
  }

  const patchBackend = (partial: Partial<ClientConfigPolicy['backendUi']>) => {
    setDraft((prev) =>
      prev ? { ...prev, backendUi: { ...prev.backendUi, ...partial } } : prev,
    )
  }

  const onSave = async () => {
    try {
      await save.mutateAsync(draft)
      toast.push({
        tone: 'success',
        message: 'Version policy saved',
        detail: 'Clients will pick this up on next start or focus.',
      })
    } catch (err) {
      toast.push({
        tone: 'error',
        message: 'Save failed',
        detail: err instanceof Error ? err.message : 'Unknown error',
      })
    }
  }

  const onReset = async () => {
    try {
      const view = await reset.mutateAsync()
      setDraft(clonePolicy(view.policy))
      toast.push({
        tone: 'success',
        message: 'Override cleared',
        detail: 'Falling back to Player-API env defaults.',
      })
    } catch (err) {
      toast.push({
        tone: 'error',
        message: 'Reset failed',
        detail: err instanceof Error ? err.message : 'Unknown error',
      })
    }
  }

  const forceThisConsole = () => {
    patchBackend({ minBuildId: thisBuildId, forceReload: true })
  }

  return (
    <div className="w7-version">
      <PageHead
        title="App version"
        subtitle="Force native updates and web cache reloads from one policy"
        actions={
          <div className="w7-version-actions">
            <Chip tone={source === 'override' ? 'warn' : 'neutral'} size="sm">
              {source === 'override' ? 'Override active' : 'Env defaults'}
            </Chip>
            {updatedAt ? (
              <span className="w7-dim" style={{ fontSize: 12 }}>
                Updated {new Date(updatedAt).toLocaleString()}
              </span>
            ) : null}
          </div>
        }
      />

      {!live ? (
        <StateBlock icon="info" title="Demo / offline">
          Connect Player-API (`VITE_PLAYER_BASE_URL` + admin key on the Vite proxy) to
          save. You can still edit the form below; Save will fail until live.
        </StateBlock>
      ) : null}

      <section className="w7-version-section">
        <h2 className="w7-version-h">Frontend-UI — Android / iOS</h2>
        <p className="w7-dim w7-version-lead">
          When force update is on and the installed SemVer is below the minimum, the
          app shows a non-dismissible update alert. Members cannot browse, bet, or
          call the API until they install the newer build.
        </p>
        <div className="w7-version-grid">
          <Field label="Minimum version" hint="SemVer, e.g. 1.2.0. Use 0.0.0 for no floor.">
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                value={draft.frontendUi.minVersion}
                onChange={(e) => patchFrontend({ minVersion: e.target.value })}
              />
            )}
          </Field>
          <Field label="Latest version" hint="Shown as the target release.">
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                value={draft.frontendUi.latestVersion}
                onChange={(e) => patchFrontend({ latestVersion: e.target.value })}
              />
            )}
          </Field>
          <Field label="iOS URL" hint="App Store or TestFlight link.">
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                value={draft.frontendUi.iosUrl}
                onChange={(e) => patchFrontend({ iosUrl: e.target.value })}
              />
            )}
          </Field>
          <Field label="Android URL" hint="Play Store or APK download.">
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                value={draft.frontendUi.androidUrl}
                onChange={(e) => patchFrontend({ androidUrl: e.target.value })}
              />
            )}
          </Field>
        </div>
        <ToggleRow
          label="Force native update"
          hint="Block all app operations until members install the minimum version"
          checked={draft.frontendUi.forceUpdate}
          onChange={(forceUpdate) => patchFrontend({ forceUpdate })}
        />
      </section>

      <section className="w7-version-section">
        <h2 className="w7-version-h">Frontend-UI — Web</h2>
        <p className="w7-dim w7-version-lead">
          Stamp builds with <code>--dart-define=APP_BUILD_ID=…</code>. When force web
          reload is on and the id differs, Flutter shows an update alert; Update now
          clears the service worker and reloads. No portal operations until then.
        </p>
        <div className="w7-version-grid">
          <Field label="Required web build id">
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                value={draft.frontendUi.webBuildId}
                onChange={(e) => patchFrontend({ webBuildId: e.target.value })}
              />
            )}
          </Field>
        </div>
        <ToggleRow
          label="Force web reload"
          hint="Show update alert and block the portal until the web client reloads"
          checked={draft.frontendUi.forceWebReload}
          onChange={(forceWebReload) => patchFrontend({ forceWebReload })}
        />
      </section>

      <section className="w7-version-section">
        <h2 className="w7-version-h">Backend-UI — Web</h2>
        <p className="w7-dim w7-version-lead">
          This console build id: <code>{thisBuildId}</code>. Stamp deploys with{' '}
          <code>VITE_APP_BUILD_ID</code>.
        </p>
        <div className="w7-version-grid">
          <Field label="Required build id">
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                value={draft.backendUi.minBuildId}
                onChange={(e) => patchBackend({ minBuildId: e.target.value })}
              />
            )}
          </Field>
        </div>
        <div className="w7-version-row">
          <ToggleRow
            label="Force console reload"
            hint="Show update alert and block the console until agents reload"
            checked={draft.backendUi.forceReload}
            onChange={(forceReload) => patchBackend({ forceReload })}
          />
          <Button variant="secondary" onClick={forceThisConsole}>
            Require this build id
          </Button>
        </div>
      </section>

      <div className="w7-version-footer">
        <Button
          variant="primary"
          busy={save.isPending}
          onClick={() => void onSave()}
          disabled={!live}
        >
          Save policy
        </Button>
        <Button
          variant="secondary"
          busy={reset.isPending}
          onClick={() => void onReset()}
          disabled={!live || source === 'env'}
        >
          Reset to env
        </Button>
      </div>
    </div>
  )
}
